import * as React from 'react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { EmptyState } from '../empty_state/EmptyState';
import MessageBubble from '../MessageBubble';
import MessageLog from '../MessageLog';
import SeparatorWithText from '../common/SeperatorWithText';
import {
	getPrimaryColor,
	getUnreadNotesIndexAndCount,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { getCalculatedChatHeight } from '../../utils/ChatUtils';
import { isNoteValid } from '../../utils/WorkOrderUtils';
import { useNotesWebSocket } from '../../hooks/useNotesWebSocket';
import { NOTE_INPUT_ID } from '../../utils/DomUtils';
import {
	NOTE_TYPE_LOG,
	NOTE_TYPE_SERVICE_CALL_CHECK_IN,
	NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
} from '../../constants/NoteTypes';
import ServiceCallLogEntry from '../ServiceCallLogEntry';

require('./CommonNotesChatPage.less');

interface WsConfig {
	entityIdName: string;
	entityType: string;
	newNoteEntityType: string;
}

interface CommonNotesChatPageProps {
	/** Channel key in state.messages (also used as entityId for WS) */
	entityId: string;
	/** All IDs to JOIN via WS. Defaults to [entityId]. */
	entityIds?: string[];
	/** Function to fetch/refresh notes. Must return a Promise. */
	fetchNotes: () => Promise<any>;
	userType: 'buyer' | 'supplier' | string;
	/** CSS class selector used for height calculation (e.g. 'buyer-workorder-detail-header') */
	heightSelector: string;
	/** Additional selectors subtracted from height calculation */
	heightExtraSelectors?: string[];
	wsConfig: WsConfig;
	currentUser: any;
	/** Redux messages state slice (state.messages) */
	messages: any;
	/** Slot: renders subscriber avatars/modals above the chat box */
	renderSubscribers?: () => React.ReactNode;
	/**
	 * Slot: renders the input form below the chat box.
	 * Receives WS helpers so the form can send typing events and broadcast new notes.
	 */
	renderInputForm?: (helpers: {
		handleTyping: () => void;
		onSendNote: (text?: string, noteType?: string) => void;
		typingDetails: any[];
		newNoteCount: number;
		onRefresh: () => void;
	}) => React.ReactNode;
	/** When true, no input form is rendered (e.g. PurchaseOrderNotesPage) */
	readOnly?: boolean;
	/** When true, input form renders in normal document flow instead of fixed positioning */
	inlineInput?: boolean;
}

const CommonNotesChatPage: FC<CommonNotesChatPageProps> = ({
	entityId,
	entityIds,
	fetchNotes,
	userType,
	heightSelector,
	heightExtraSelectors = [],
	wsConfig,
	currentUser,
	messages,
	renderSubscribers,
	renderInputForm,
	readOnly = false,
	inlineInput = false,
}) => {
	const [fetching, setFetching] = useState(true);
	const [notesList, setNotesList] = useState<any[]>([]);
	const [calculatedHeight, setCalculatedHeight] = useState(0);
	const [refreshCount, setRefreshCount] = useState(0);
	const [chatBoxPaddingBottom, setChatBoxPaddingBottom] = useState(0);

	const prevMessagesRef = useRef<any[]>([]);
	const noteInputResizeObserverRef = useRef<ResizeObserver | null>(null);
	const entityIdsForWs = entityIds || [entityId];

	const scrollToBottom = useCallback(() => {
		const el = document.getElementById('chatBox');
		if (el) el.scrollTop = el.scrollHeight;
	}, []);

	const refreshMessages = useCallback(() => {
		return fetchNotes().then((res) => {
			setRefreshCount((c) => c + 1);
			return res;
		});
	}, [fetchNotes]);

	const { typingDetails, newNoteCount, resetNewNoteCount, handleTyping, onSendNote } =
		useNotesWebSocket({
			entityIds: entityIdsForWs,
			currentEntityId: entityId,
			entityIdName: wsConfig.entityIdName,
			entityType: wsConfig.entityType,
			newNoteEntityType: wsConfig.newNoteEntityType,
			refreshMessages,
			currentUser,
		});

	const handleRefresh = useCallback(() => {
		refreshMessages().then(() => {
			resetNewNoteCount();
			scrollToBottom();
		});
	}, [resetNewNoteCount, refreshMessages, scrollToBottom]);

	const updateHeight = useCallback(() => {
		setCalculatedHeight(getCalculatedChatHeight(heightSelector, heightExtraSelectors));
	}, [heightSelector, heightExtraSelectors]);

	const setupNoteInputObserver = useCallback(() => {
		const inputEl = document.getElementById(NOTE_INPUT_ID);
		if (!inputEl) return;

		const updatePadding = () => {
			setChatBoxPaddingBottom(inputEl.offsetHeight);
		};

		updatePadding();
		// Defer ResizeObserver updates to next frame to avoid
		// "ResizeObserver loop completed with undelivered notifications" errors
		// that happen when the callback triggers layout changes synchronously.
		noteInputResizeObserverRef.current = new ResizeObserver(() => {
			window.requestAnimationFrame(updatePadding);
		});
		noteInputResizeObserverRef.current.observe(inputEl);
	}, []);

	// Initial load + window resize listener (runs once on mount)
	useEffect(() => {
		updateHeight();
		window.addEventListener('resize', updateHeight);
		return () => {
			window.removeEventListener('resize', updateHeight);
			if (noteInputResizeObserverRef.current) {
				noteInputResizeObserverRef.current.disconnect();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch notes whenever entityId changes (e.g. user switches bidders).
	// Resets per-channel state so stale notes from the previous entity don't leak.
	useEffect(() => {
		setFetching(true);
		setNotesList([]);
		setRefreshCount(0);
		prevMessagesRef.current = [];
		fetchNotes().then(() => {
			setFetching(false);
			setTimeout(() => {
				scrollToBottom();
				setupNoteInputObserver();
			}, 0);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entityId]);

	// Update height when layout-related props change
	useEffect(() => {
		updateHeight();
	}, [heightSelector, heightExtraSelectors, updateHeight]);

	// Sync notes from Redux messages state
	useEffect(() => {
		const newNotes = nullSafeGetOrElse(`${entityId}.messages`, messages, []);
		if (newNotes !== prevMessagesRef.current && newNotes.length > 0) {
			prevMessagesRef.current = newNotes;
			setNotesList(newNotes);
		}
	}, [messages, entityId]);

	// Scroll to bottom after notesList has been committed to the DOM.
	// Running this in a separate effect ensures scrollHeight reflects the new
	// notes before we measure — otherwise scrollToBottom would use the pre-update
	// scrollHeight and leave the user one note short of the actual bottom.
	useEffect(() => {
		if (notesList.length > 0) {
			scrollToBottom();
		}
	}, [notesList, scrollToBottom]);

	const actualNotes = notesList.filter(isNoteValid);

	const { firstUnreadMessageIndex, unreadMessageLength } = getUnreadNotesIndexAndCount(
		actualNotes,
		currentUser
	);

	if (fetching) {
		return <PageLoadingPlaceholder />;
	}

	return (
		<div
			style={{ height: `${calculatedHeight}px`, maxHeight: `${calculatedHeight}px` }}
			className="commonNotesChatPage"
		>
			{renderSubscribers && (
				<div className="flex flex-row justify-end pr-4">{renderSubscribers()}</div>
			)}
			<div
				id="chatBox"
				style={{ flexGrow: 1, overflowY: 'auto', paddingBottom: chatBoxPaddingBottom }}
				className="no-scrollbar"
			>
				{actualNotes.length > 0 ? (
					<div className="chatList">
						{actualNotes.map((n, idx) => (
							<React.Fragment key={`note-${idx}`}>
								{firstUnreadMessageIndex === idx ? (
									<SeparatorWithText
										className="mt-2 px-8"
										text={`${unreadMessageLength} ${refreshCount > 1 ? 'new' : 'unread'} message${
											unreadMessageLength > 1 ? 's' : ''
										}`}
										textColor={getPrimaryColor(userType)}
										bgColor={getPrimaryColor(userType)}
									/>
								) : null}
								{n.type === NOTE_TYPE_LOG ? (
									<MessageLog log={n} />
								) : n.type === NOTE_TYPE_SERVICE_CALL_CHECK_IN ||
								  n.type === NOTE_TYPE_SERVICE_CALL_CHECK_OUT ? (
									<ServiceCallLogEntry entry={n} type={n.type} />
								) : (
									<MessageBubble
										currentUser={currentUser}
										n={n}
										idx={idx}
										subscribers={[]}
										userType={userType}
										key={idx}
									/>
								)}
							</React.Fragment>
						))}
					</div>
				) : (
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
								alt="No notes have been added yet"
							/>
						}
						headline={'No notes have been added yet'}
					/>
				)}
			</div>
			{!readOnly && renderInputForm && (
				<div id={inlineInput ? undefined : NOTE_INPUT_ID}>
					{renderInputForm({
						handleTyping,
						onSendNote,
						typingDetails,
						newNoteCount,
						onRefresh: handleRefresh,
					})}
				</div>
			)}
		</div>
	);
};

export default CommonNotesChatPage;
